// See also: src/components/RoutesComponents.tsx

import { encodeTag, encodeTags } from "@/common/utils/strings";

interface IUrlParams {
  [key: string]: string | undefined | boolean;
}

export interface ISearchParams extends IUrlParams {
  order?: string;
  verified?: boolean;
  query?: string;
}

interface INichesParams extends IUrlParams {
  order?: string;
  category?: string;
}

export interface INicheParams extends IUrlParams {
  order?: string;
  gif?: string;
  source?: string;
}

interface ISearchTagParams {
  order?: string;
  tab?: string;
  verified?: boolean;
  gif?: string;
}

export enum FreetypeSearchType {
  gifs = "gifs",
  images = "images",
  creators = "creators",
  niches = "niches"
}

export enum ExploreType {
  gifs = "gifs",
  images = "images",
  creators = "creators",
  niches = "niches"
}

const buildUrl = (path: string, params?: IUrlParams) => {
  if (!params) {
    return path;
  }

  const queryParams = new URLSearchParams();

  for (const key in params) {
    let value = params[key];
    if (typeof value === "boolean") {
      value = value ? "1" : "";
    }

    if (!value) {
      continue;
    }

    queryParams.append(key, value);
  }

  return `${path}${queryParams.toString().length ? `?${queryParams.toString()}` : ""}`;
};

export const routes = {
  accountSettings: () => "/settings/account",
  collection: (userId: string, id: string) => `/users/${userId}/collections/${id}`,
  crash: () => "/crash",
  create: () => "/create",
  dashboard: () => "/dashboard",
  app: () => "/app",
  embed: (id: string) => `/ifr/${id}`,
  creators: (params?: ISearchParams) => buildUrl("/creators", params),
  favourites: () => "/gifs/favourites",
  faq: () => "/faq",
  following: () => "/following",
  followers: () => "/followers",
  home: (videoId?: string) => `/${videoId ? (`?video=${videoId}`) : ""}`,
  manageProfile: (tab?: string, count?: string) => {
    const queryParams = new URLSearchParams();

    if (tab) {
      queryParams.append("tab", tab);
    }

    if (count) {
      queryParams.append("count", count);
    }

    const params = queryParams.toString().length ? `?${queryParams.toString()}` : "";

    return `/profile/manage${params}`;
  },
  myNiches: () => "/niches/my",
  niche: (id: string, params?: INicheParams) => buildUrl(`/niches/${id}`, params),
  niches: (params?: INichesParams) => buildUrl("/niches", params),
  profileSettings: () => "/settings/profile",
  signup: () => "/signup",
  singleTag: (tag: string, params?: ISearchTagParams) =>
    buildUrl(`/gifs/${encodeTag(tag)}`, { ...params }),
  multipleTag: (tag: string[], params?: ISearchParams) =>
    buildUrl(`/gifs/${encodeTags(tag)}`, { ...params }),
  tagContent: (tag: string) => `/tag/${encodeTag(tag)}`,
  freetypeSearch: (type?: FreetypeSearchType, params?: ISearchParams) => buildUrl(`/search/${type ?? ":type"}`, params),
  explore: (type?: ExploreType, params?: ISearchParams) => buildUrl(`/explore/${type ?? ":type"}`, { ...params }),
  sudo: () => "/sudo",
  trendingTags: () => "/categories",
  trendingTagsA: (a: string) => `/categories/${a}`,
  unsubscribed: () => "/unsubscribed",
  user: (id: string, order?: string) => buildUrl(`/users/${id}`, { order }),
  userCollections: (userId: string) => `/users/${userId}/collections`,
  verify: () => "/user/verify",
  authorize: () => "/authorize",
  logout: () => "/logout",
  experiments: () => "/experiments",
  watch: (id: string, rel?: string, source?: string, order?: string, verified?: boolean) => {
    const relPart = rel ? `#rel=${encodeURIComponent(rel)}` : "";
    const sourcePart = source ? `?source=${source}` : "";
    const orderPart = order ? `;order=${order}` : "";
    const verifiedPart = verified ? `;verified=${verified}` : "";
    const resultPart = relPart ? `${relPart}${orderPart}${verifiedPart}` : `${sourcePart}`;

    return `/watch/${id}${resultPart}`;
  },
  manageAdSlots: () => "/admin/ads",
  geoBlockedPage: (location: string) => `/${location.replace(" ", "-")}-slate`,
  searchWarningPage: () => "/search-warning",
  userSignIn: () => "/auth/signin",
  userSignUp: () => "/auth/signup",
  partyTown: () => "/~partytown/*",
};

const GIF_PAGE_PATH = /^\/watch\/[^/]+\/?$/;
const CREATOR_PAGE_PATH = /^\/users\/[^/]+\/?$/;

export const isGridTargetPath = (pathname: string, search: string): boolean =>
  GIF_PAGE_PATH.test(pathname)
  || CREATOR_PAGE_PATH.test(pathname)
  || new URLSearchParams(search).has("gif");

export const isEmbedPath = (pathname: string): boolean => pathname.startsWith("/ifr/");

const EMBED_REFERRAL_PARAM = "from";
const EMBED_REFERRAL_VALUE = "embed";

export const embedGifLink = (id: string): string =>
  `${routes.watch(id)}?${EMBED_REFERRAL_PARAM}=${EMBED_REFERRAL_VALUE}`;

export const isEmbedReferral = (search: string): boolean =>
  new URLSearchParams(search).get(EMBED_REFERRAL_PARAM) === EMBED_REFERRAL_VALUE;

export type TileTarget = "gif" | "niche" | "creator";

export const getTileTarget = (href: string): TileTarget | null => {
  const { pathname } = new URL(href, window.location.origin);

  if (GIF_PAGE_PATH.test(pathname)) {
    return "gif";
  }

  if (pathname.startsWith("/niches/")) {
    return "niche";
  }

  if (pathname.startsWith("/users/")) {
    return "creator";
  }

  return null;
};
